import type {
  DragEndEvent,
  DragMoveEvent,
  PointerSensorOptions,
  TouchSensorOptions,
} from '@dnd-kit/core'
import {
  PointerSensor,
  TouchSensor,
} from '@dnd-kit/core'
import type { PointerEvent as ReactPointerEvent, TouchEvent as ReactTouchEvent } from 'react'

export type DropTargetPlacement = 'before' | 'after'
export type PointerCoordinates = { x: number; y: number }

// Drag pickup thresholds, shared by every trip planner drag surface so the feel
// can't drift between them.
//
// Mouse/pen go through the pointer sensor: a small movement buffer so a click on a
// card body still opens it, while a deliberate drag activates quickly.
//
// Touch goes through the touch sensor (see `createDragEventSensors`) and must be a
// long press, so a finger swipe is always a scroll and never grabs a card. No
// draggable sets `touch-action: none`, which is what keeps those lists scrollable.
export const POINTER_DRAG_ACTIVATION_DISTANCE = 6
export const TOUCH_DRAG_ACTIVATION_DELAY = 250
export const TOUCH_DRAG_ACTIVATION_TOLERANCE = 5
export const DROP_ANIMATION_DURATION = 150

/**
 * Id for the copy of a card rendered inside `DragOverlay`.
 *
 * dnd-kit keys draggable nodes in a Map that outlives any single drag, and
 * `useDraggable` registers into it even when `disabled`. If the overlay copy shared
 * the real card's id it would overwrite that entry on mount, then delete it on unmount
 * (the cleanup's key guard matches, because the overlay wrote last) - leaving the real
 * card unregistered and permanently undraggable. A distinct id keeps the two entries
 * independent. See `dragOverlayDraggableId.test.tsx`.
 */
export function getDragOverlayDraggableId(id: string | number): string {
  return `${id}__drag-overlay`
}

// Elements that should swallow a pointer-down without starting a drag, so links,
// buttons, form controls and editable regions keep their native behaviour. Each drag
// surface prepends its own `data-*-drag-disabled` opt-out attribute to this base.
export const INTERACTIVE_DRAG_IGNORE_SELECTORS = ['a', 'button', 'input', 'select', 'textarea', '[contenteditable="true"]']

// Builds pointer/touch sensors that ignore drags starting on interactive elements
// (links, buttons, inputs, anything matching `disabledSelector`) so clicks and text
// selection keep working. Shared by every drag surface so the activation behaviour
// can't drift between them - only the disabled selector differs per surface.
export function createDragEventSensors(disabledSelector: string) {
  const isDragEventTarget = (target: EventTarget | null): boolean =>
    target instanceof Element && !target.closest(disabledSelector)

  class DragPointerSensor extends PointerSensor {
    static activators = [{
      eventName: 'onPointerDown' as const,
      handler: (event: ReactPointerEvent, options: PointerSensorOptions): boolean => {
        if (!isDragEventTarget(event.nativeEvent.target)) return false
        // Leave touch to DragTouchSensor. `pointerdown` fires before `touchstart`, and
        // dnd-kit only instantiates the first sensor to claim a gesture, so without this
        // the pointer sensor wins every touch drag and the touch sensor's long-press
        // delay never runs - making a 6px finger slide race the browser's scroll.
        if (event.nativeEvent.pointerType === 'touch') return false
        return PointerSensor.activators[0].handler(event, options)
      },
    }]
  }

  class DragTouchSensor extends TouchSensor {
    static activators = [{
      eventName: 'onTouchStart' as const,
      handler: (event: ReactTouchEvent, options: TouchSensorOptions): boolean => {
        if (!isDragEventTarget(event.nativeEvent.target)) return false
        return TouchSensor.activators[0].handler(event, options)
      },
    }]
  }

  return { PointerSensor: DragPointerSensor, TouchSensor: DragTouchSensor }
}

export function moveItemToDropTarget<T>(items: Array<T>, item: T, targetItem: T, placement: DropTargetPlacement): Array<T> {
  if (item === targetItem) return items

  const nextItems = items.filter(currentItem => currentItem !== item)
  const targetIndex = nextItems.indexOf(targetItem)
  if (targetIndex === -1) return items
  nextItems.splice(placement === 'after' ? targetIndex + 1 : targetIndex, 0, item)
  return nextItems
}

export function moveItemToGroupTarget<T>(items: Array<T>, item: T, targetItems: Array<T>, placement: DropTargetPlacement): Array<T> {
  const nextItems = items.filter(currentItem => currentItem !== item)
  const targetIndexes = targetItems
    .map(targetItem => nextItems.indexOf(targetItem))
    .filter(index => index !== -1)
  if (targetIndexes.length === 0) return [...nextItems, item]

  const insertionIndex = placement === 'before' ? Math.min(...targetIndexes) : Math.max(...targetIndexes) + 1
  nextItems.splice(insertionIndex, 0, item)
  return nextItems
}

export function getPointerCoordinates(event: Event): PointerCoordinates | undefined {
  if ('clientX' in event && 'clientY' in event) {
    return {
      x: Number(event.clientX),
      y: Number(event.clientY),
    }
  }

  if ('touches' in event) {
    const touches = (event as TouchEvent).touches
    if (touches.length === 0) return undefined

    const touch = touches[0]
    return { x: touch.clientX, y: touch.clientY }
  }

  return undefined
}

export function getDropPointFromDragEvent(
  event: DragMoveEvent | DragEndEvent,
  origin: PointerCoordinates | undefined,
  centerOffset: PointerCoordinates | undefined,
): PointerCoordinates | undefined {
  const translatedRect = event.active.rect.current.translated
  if (translatedRect) {
    return {
      x: translatedRect.left + translatedRect.width / 2,
      y: translatedRect.top + translatedRect.height / 2,
    }
  }

  if (!origin) return undefined

  return {
    x: origin.x + event.delta.x + (centerOffset?.x ?? 0),
    y: origin.y + event.delta.y + (centerOffset?.y ?? 0),
  }
}
